"use client";

import {
  ArrowClockwise,
  ArrowCounterClockwise,
  Code as CodeIcon,
  LinkSimple,
  ListBullets,
  ListNumbers,
  TextAUnderline,
  TextB,
  TextHOne,
  TextHThree,
  TextHTwo,
  TextIndent,
  TextItalic,
  TextOutdent,
} from "@phosphor-icons/react";
import { cn } from "@resume-studio/utils";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Code } from "@tiptap/extension-code";
import { Document } from "@tiptap/extension-document";
import { Heading } from "@tiptap/extension-heading";
import { History } from "@tiptap/extension-history";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Underline } from "@tiptap/extension-underline";
import { type Editor, EditorContent, type EditorContentProps, useEditor } from "@tiptap/react";
import { forwardRef, useCallback, useEffect, useRef } from "react";

import { Button } from "./button";
import { Skeleton } from "./skeleton";
import { Toggle } from "./toggle";
import { TooltipWrapper } from "./tooltip-wrapper";

const Toolbar = ({ editor }: { editor: Editor }) => {
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
      <TooltipWrapper content="Bold">
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <TextB className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Italic">
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <TextItalic className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Underline">
        <Toggle
          size="sm"
          pressed={editor.isActive("underline")}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
        >
          <TextAUnderline className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Heading 1">
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 1 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 1 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <TextHOne className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Heading 2">
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 2 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 2 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <TextHTwo className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Heading 3">
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 3 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 3 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <TextHThree className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Bullet List">
        <Toggle
          size="sm"
          pressed={editor.isActive("bulletList")}
          disabled={!editor.can().chain().focus().toggleBulletList().run()}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListBullets className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Numbered List">
        <Toggle
          size="sm"
          pressed={editor.isActive("orderedList")}
          disabled={!editor.can().chain().focus().toggleOrderedList().run()}
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListNumbers className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Inline Code">
        <Toggle
          size="sm"
          pressed={editor.isActive("code")}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          onPressedChange={() => editor.chain().focus().toggleCode().run()}
        >
          <CodeIcon className="size-4" />
        </Toggle>
      </TooltipWrapper>

      <TooltipWrapper content="Hyperlink">
        <Button type="button" size="sm" variant="ghost" className="px-2" onClick={setLink}>
          <LinkSimple className="size-4" />
        </Button>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Increase Indent">
        <Button
          size="sm"
          variant="ghost"
          className="px-2"
          disabled={!editor.can().chain().focus().sinkListItem("listItem").run()}
          onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
        >
          <TextIndent className="size-4" />
        </Button>
      </TooltipWrapper>

      <TooltipWrapper content="Decrease Indent">
        <Button
          size="sm"
          variant="ghost"
          className="px-2"
          disabled={!editor.can().chain().focus().liftListItem("listItem").run()}
          onClick={() => editor.chain().focus().liftListItem("listItem").run()}
        >
          <TextOutdent className="size-4" />
        </Button>
      </TooltipWrapper>

      <div className="mx-1 h-6 w-px bg-border" />

      <TooltipWrapper content="Undo">
        <Button
          size="sm"
          variant="ghost"
          className="px-2"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <ArrowCounterClockwise className="size-4" />
        </Button>
      </TooltipWrapper>

      <TooltipWrapper content="Redo">
        <Button
          size="sm"
          variant="ghost"
          className="px-2"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <ArrowClockwise className="size-4" />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

type RichInputProps = {
  content?: string;
  onChange?: (value: string) => void;
  hideToolbar?: boolean;
  className?: string;
  editorClassName?: string;
  footer?: (editor: Editor) => React.ReactNode;
  floatingAI?: { button: React.ReactNode | ((editor: Editor) => React.ReactNode) };
} & Omit<EditorContentProps, "ref" | "editor" | "content" | "value" | "onChange" | "className">;

export const RichInput = forwardRef<Editor, RichInputProps>(
  (
    {
      content,
      onChange,
      footer,
      hideToolbar = false,
      className,
      editorClassName = "min-h-[300px]",
      floatingAI,
      ...props
    },
    _ref,
  ) => {
    const editor = useEditor({
      extensions: [
        Document,
        Text,
        Bold,
        Italic,
        Code,
        Heading,
        Paragraph,
        ListItem,
        BulletList,
        OrderedList,
        History,
        Underline,
        Link.extend({
          inclusive: false,
          addKeyboardShortcuts: () => ({
            "Mod-k": () => setLink(),
          }),
        }).configure({ openOnClick: false }),
      ],
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm prose-zinc max-w-none overflow-y-auto dark:prose-invert focus:outline-none [&_*]:my-2",
            editorClassName,
          ),
        },
      },
      content,
      parseOptions: { preserveWhitespace: "full" },
      onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    });

    // Sync editor when external content prop changes (e.g., AI updates store)
    const lastAppliedContentRef = useRef<string | undefined>(undefined);
    const toEditorHtml = useCallback((value: string): string => {
      const s = value ?? "";
      const looksLikeHtml = /<\w[\s\S]*>/i.test(s);
      if (looksLikeHtml) return s;
      // Minimal escape for plain text
      const escaped = s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
      return `<p>${escaped}</p>`;
    }, []);
    useEffect(() => {
      if (!editor) return;

      const next = content ?? "";
      const currentHtml = editor.getHTML();
      const nextHtml = toEditorHtml(next);

      if (currentHtml !== nextHtml) {
        try {
          // Do not emit onUpdate for external sync to avoid feedback loops
          // Preserve whitespace for consistent parsing
          editor.commands.setContent(nextHtml, false, { preserveWhitespace: "full" });
          lastAppliedContentRef.current = next;
        } catch {
          // noop – avoid throwing in render if editor isn't ready
        }
      }
    }, [editor, content, toEditorHtml]);

    const setLink = useCallback(() => {
      if (!editor) return false;

      const previousUrl = editor.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

      // cancelled
      if (url === null) return false;

      // empty
      if (url === "") {
        return editor.chain().focus().extendMarkRange("link").unsetLink().run();
      }

      // update link
      return editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }, [editor]);

    if (!editor) {
      return (
        <div className="space-y-2">
          <Skeleton className={cn("h-[42px] w-full", hideToolbar && "hidden")} />
          <Skeleton className="h-[90px] w-full" />
        </div>
      );
    }

    return (
      <div className="relative">
        {!hideToolbar && <Toolbar editor={editor} />}

        <div className="relative">
          <EditorContent
            editor={editor}
            className={cn(
              "prose prose-sm min-h-[300px] w-full max-w-none cursor-text overflow-y-auto border bg-secondary px-3 py-2 text-sm placeholder:opacity-80 hover:cursor-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 [&_*]:my-1 [&_.ProseMirror]:min-h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:outline-none",
              hideToolbar && "pt-2",
              floatingAI && "pb-12 pr-14",
              className,
            )}
            {...props}
          />

          {floatingAI && (
            <div className="pointer-events-auto absolute bottom-3 right-3 z-10">
              <div className="rounded border bg-background/95 shadow-sm backdrop-blur-sm">
                {typeof floatingAI.button === "function"
                  ? (floatingAI.button as any)(editor)
                  : floatingAI.button}
              </div>
            </div>
          )}
        </div>

        {footer?.(editor)}
      </div>
    );
  },
);

RichInput.displayName = "RichInput";
